import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, apiFetch, type ApiError } from '../../api/client';
import { Alert, Field } from '../../components/Field';
import { StatusPill } from '../../components/StatusPill';
import { ImportFromDocument } from '../../components/ImportFromDocument';
import { settleUpload } from '../../lib/content-workflow';
import { ConfirmDialog, type PendingDelete } from '../../components/ConfirmDialog';
import { useAuth } from '../../auth/AuthContext';
import { duration, LESSON_ICON } from '../../lib/format';

interface Lesson {
  id: string;
  title: string;
  type: string;
  durationMinutes: number;
  isRequired: boolean;
  isFreePreview: boolean;
  contentItemId: string | null;
  contentStatus: string | null;
  contentKind: string | null;
  externalUrl: string | null;
}

interface Module {
  id: string;
  title: string;
  description: string | null;
  lessons: Lesson[];
}

interface CourseDetail {
  id: string;
  title: string;
  slug: string;
  status: string;
  shortDescription: string;
  longDescription: string | null;
  durationMinutes: number;
  thumbnailId: string | null;
  thumbnailStatus: string | null;
  priceMinor: number;
  currency: string;
  isFree: boolean;
  navigationMode: 'FREE' | 'SEQUENTIAL';
  modules: Module[];
  publishReadiness: { canPublish: boolean; issues: Array<{ field: string; message: string }> };
}

interface ContentOption {
  id: string;
  title: string;
  kind: string;
  status: string;
  /** False while a file is quarantined, which means it will not be served. */
  servable?: boolean;
  scanStatus?: string;
}

const TITLE_MAX = 250;
/** Matches the API's limit, and the column's. */
const MODULE_DESCRIPTION_MAX = 1000;

const LESSON_TYPES = ['RICH_TEXT', 'DOCUMENT', 'PRESENTATION', 'VIDEO', 'CODE', 'EXTERNAL_LINK'];

/**
 * Which library items each lesson type can play — the API's
 * `LESSON_CONTENT_KINDS`, repeated so the picker only offers what will be
 * accepted. An empty list is a link lesson: a URL, and no content item.
 */
const LESSON_KINDS: Record<string, string[]> = {
  VIDEO: ['VIDEO'],
  DOCUMENT: ['DOCUMENT'],
  PRESENTATION: ['PRESENTATION', 'DOCUMENT'],
  RICH_TEXT: ['RICH_TEXT'],
  CODE: ['CODE', 'RICH_TEXT'],
  EXTERNAL_LINK: [],
  EMBED: [],
};

const isLinkType = (type: string) => LESSON_KINDS[type]?.length === 0;

/** How long a success message stays before it gets out of the way. */
const NOTICE_MS = 5000;

/**
 * Which workflow buttons apply in which state — the API's transition table
 * (`transitionRefusal`). An instructor submits a draft; a publisher, who is
 * the approver, may publish it directly. An archived course was live once
 * and may go straight back.
 */
const CAN_SUBMIT = ['DRAFT', 'ARCHIVED'];
const CAN_PUBLISH = ['DRAFT', 'REVIEW', 'APPROVED', 'ARCHIVED'];

export function CourseBuilder() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  // The API refuses anything a role may not do; these only stop the page
  // offering it. An instructor used to see Publish and Delete course and get
  // "Access denied" for pressing them, and a reviewer saw every edit control.
  const { can } = useAuth();
  const mayEditCourse = can('COURSE_UPDATE');
  const mayManageModules = can('MODULE_MANAGE');
  const mayManageLessons = can('LESSON_MANAGE');
  const maySubmit = can('CONTENT_SUBMIT_REVIEW');
  const mayPublish = can('COURSE_PUBLISH');
  const mayDelete = can('COURSE_DELETE');
  const mayImport = mayManageLessons && can('CONTENT_UPLOAD');
  const [course, setCourse] = useState<CourseDetail | null>(null);
  const [content, setContent] = useState<ContentOption[]>([]);
  const [images, setImages] = useState<ContentOption[]>([]);
  /** PDFs the import can read. Narrowed here rather than in the panel. */
  const [documents, setDocuments] = useState<ContentOption[]>([]);
  const [error, setError] = useState<ApiError | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [moduleTitle, setModuleTitle] = useState('');
  const [moduleDescription, setModuleDescription] = useState('');
  /** The module whose name and description are open for editing, or none. */
  const [editingModule, setEditingModule] = useState<string | null>(null);
  /** The deletion waiting to be confirmed, or none. */
  const [pending, setPending] = useState<PendingDelete | null>(null);
  /** The lesson open for editing, or none. */
  const [editingLesson, setEditingLesson] = useState<string | null>(null);

  // "Module saved." used to stay on screen for the rest of the visit, so it
  // sat above the next error and read as though that had worked too. Every
  // action clears it as it starts; this clears it when nothing follows.
  useEffect(() => {
    if (!notice) return;
    const timer = window.setTimeout(() => setNotice(null), NOTICE_MS);
    return () => window.clearTimeout(timer);
  }, [notice]);

  const load = useCallback(() => {
    api<CourseDetail>(`/admin/courses/${id}`)
      .then(setCourse)
      .catch((caught) => setError(caught as ApiError));
  }, [id]);

  useEffect(load, [load]);

  const loadImages = useCallback(() => {
    // Only images, and only the ones that can actually be served: a
    // quarantined file would be accepted as a cover and then show nothing.
    api<{ items: ContentOption[] }>('/content?kind=IMAGE')
      .then((result) => setImages(result.items))
      .catch(() => setImages([]));
  }, []);

  useEffect(() => {
    api<{ items: ContentOption[] }>('/content')
      .then((result) => setContent(result.items))
      .catch(() => setContent([]));
    api<{ items: ContentOption[] }>('/content?kind=DOCUMENT')
      .then((result) => setDocuments(result.items))
      .catch(() => setDocuments([]));
  }, []);

  useEffect(loadImages, [loadImages]);

  /** After a cover changes, both the course and the picker are out of date. */
  const reload = useCallback(() => {
    load();
    loadImages();
  }, [load, loadImages]);

  /** Posts, then reloads. Returns false on failure, so a form keeps what was typed. */
  async function act(path: string, body?: unknown, message?: string): Promise<boolean> {
    setError(null);
    setNotice(null);
    try {
      await api(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined });
      if (message) setNotice(message);
      load();
      return true;
    } catch (caught) {
      setError(caught as ApiError);
      return false;
    }
  }

  /**
   * Remove a module, a lesson or the whole course, after asking.
   *
   * Separate from `act`, which posts: all three are DELETE routes. The
   * module Delete button used to call `act`, so it sent a POST to a DELETE
   * route and could only ever fail — and it was left permanently `disabled`
   * instead of being corrected. A lesson had no delete button at all, which
   * meant a mistyped lesson could not be removed from the interface, and a
   * course carrying one could not be made publishable.
   *
   * Both are soft deletes on the server: `deletedAt` is set, the row keeps
   * its audit entry, and nothing is destroyed. None of that is reachable
   * from here though, so the caller asks first, through `ConfirmDialog`.
   *
   * It asks through a dialog of our own rather than `window.confirm`, which
   * is not reliably available: an embedded webview suppresses it, and Chrome
   * suppresses it for the rest of the page once somebody ticks "prevent this
   * page from creating additional dialogs". In both cases it returns false
   * without showing anything, so a button guarded by it silently does
   * nothing at all — which is exactly how this shipped the first time.
   */
  async function remove(path: string, message: string, leaves = false) {
    setError(null);
    setNotice(null);
    try {
      await api(path, { method: 'DELETE' });
      // Deleting the course itself removes the thing this page is showing,
      // so there is nothing left to reload: go back to the list and say so
      // there, rather than reloading into a 404.
      if (leaves) {
        navigate('/admin/courses', { state: { notice: message } });
        return;
      }
      setNotice(message);
      load();
    } catch (caught) {
      setError(caught as ApiError);
    }
  }

  async function addModule(event: FormEvent) {
    event.preventDefault();
    if (!moduleTitle.trim()) return;
    const added = await act(
      `/admin/courses/${id}/modules`,
      { title: moduleTitle, description: moduleDescription.trim() || undefined },
      'Module added.',
    );
    if (added) {
      setModuleTitle('');
      setModuleDescription('');
    }
  }

  /** For the panels below: a failure replaces any success still showing. */
  function fail(caught: ApiError) {
    setNotice(null);
    setError(caught);
  }

  /** And a success clears the error it may have just fixed. */
  function succeed(message: string) {
    setError(null);
    setNotice(message);
    load();
  }

  /** Reorder sends the full id list, so the server can reject a partial move. */
  async function move(module: Module, index: number, delta: number) {
    if (!course) return;
    const ids = module.lessons.map((lesson) => lesson.id);
    const target = index + delta;
    if (target < 0 || target >= ids.length) return;
    [ids[index], ids[target]] = [ids[target]!, ids[index]!];
    await act(`/admin/courses/${id}/modules/${module.id}/lessons/reorder`, { ids });
  }

  if (!course) {
    return error ? (
      <Alert kind="error" title={error.message} />
    ) : (
      <div className="spinner">Loading…</div>
    );
  }

  return (
    <>
      <div className="page__head">
        <p style={{ margin: 0, fontSize: 13 }}>
          <Link to="/admin/courses">← All courses</Link>
        </p>
        <h1 className="page__title" style={{ marginTop: 6 }}>
          {course.title} <StatusPill status={course.status} />
        </h1>
        <p className="page__lede">
          {course.modules.length} module{course.modules.length === 1 ? '' : 's'} ·{' '}
          {course.modules.reduce((sum, module) => sum + module.lessons.length, 0)} lessons ·{' '}
          {duration(course.durationMinutes)}
        </p>
      </div>

      {course.status === 'PUBLISHED' && (mayEditCourse || mayManageModules || mayManageLessons) ? (
        <Alert
          kind="info"
          title="This course is live. Changes save straight to what learners see, and a change that would leave it unpublishable is refused."
        />
      ) : null}
      {error ? <Alert kind="error" title={error.message} items={error.details} /> : null}
      {notice ? <Alert kind="success" title={notice} /> : null}

      {pending ? (
        <ConfirmDialog
          pending={pending}
          onCancel={() => setPending(null)}
          onConfirm={async () => {
            const { path, message, leaves } = pending;
            setPending(null);
            await remove(path, message, leaves);
          }}
        />
      ) : null}

      <div className="builder">
        <div>
          <div className="card" style={{ marginBottom: 18 }}>
            <div className="card__head">
              <span className="card__title">Curriculum</span>
            </div>

            {course.modules.length === 0 ? (
              <div className="empty">No modules yet. Add the first one below.</div>
            ) : (
              course.modules.map((module) => (
                <div key={module.id}>
                  <div className="modrow">
                    <span className="modrow__title">
                      {module.title}
                      <span className="modrow__count">
                        {module.lessons.length} lesson{module.lessons.length === 1 ? '' : 's'}
                      </span>
                    </span>
                    {mayManageModules ? (
                      <span className="modrow__actions">
                        <button
                          className="iconbtn"
                          type="button"
                          aria-expanded={editingModule === module.id}
                          onClick={() =>
                            setEditingModule(editingModule === module.id ? null : module.id)
                          }
                        >
                          Edit
                        </button>
                        <button
                          className="iconbtn iconbtn--danger"
                          onClick={() =>
                            setPending({
                              path: `/admin/courses/${course.id}/modules/${module.id}`,
                              title: 'Delete this module?',
                              detail:
                                module.lessons.length === 0
                                  ? `“${module.title}” will be removed from the curriculum.`
                                  : `“${module.title}” and its ${module.lessons.length} lesson${
                                      module.lessons.length === 1 ? '' : 's'
                                    } will be removed from the curriculum.`,
                              message: 'Module deleted.',
                            })
                          }
                          title="Deleting a module is not reversible from here"
                        >
                          Delete
                        </button>
                      </span>
                    ) : null}
                  </div>

                  {editingModule === module.id && mayManageModules ? (
                    <ModuleEditor
                      courseId={course.id}
                      module={module}
                      onSaved={() => {
                        setEditingModule(null);
                        succeed('Module saved.');
                      }}
                      onCancel={() => setEditingModule(null)}
                      onError={fail}
                    />
                  ) : module.description ? (
                    <p className="modrow__desc">{module.description}</p>
                  ) : null}

                  {module.lessons.map((lesson, index) => (
                    <div key={lesson.id}>
                      <div className="lessonrow">
                        <span aria-hidden="true">{LESSON_ICON[lesson.type] ?? '•'}</span>
                        {/* The title alone truncates; the badges sit outside it
                          so a long title cannot push them off the row, and
                          the full text is there on hover. */}
                        <span className="lessonrow__name" title={lesson.title}>
                          {lesson.title}
                        </span>
                        <span className="lessonrow__meta">
                          {lesson.isFreePreview ? (
                            <span className="pill pill--pending" style={{ marginRight: 8 }}>
                              Preview
                            </span>
                          ) : null}
                          {!lesson.isRequired ? 'optional · ' : null}
                          {lesson.contentStatus && lesson.contentStatus !== 'PUBLISHED'
                            ? `content ${lesson.contentStatus.toLowerCase()} · `
                            : null}
                          {duration(lesson.durationMinutes)}
                        </span>
                        {mayManageLessons ? (
                          <>
                            <button
                              className="iconbtn"
                              type="button"
                              aria-expanded={editingLesson === lesson.id}
                              aria-label={`Edit the lesson ${lesson.title}`}
                              onClick={() =>
                                setEditingLesson(editingLesson === lesson.id ? null : lesson.id)
                              }
                            >
                              Edit
                            </button>
                            <button
                              className="iconbtn"
                              onClick={() => move(module, index, -1)}
                              disabled={index === 0}
                              aria-label="Move up"
                            >
                              ↑
                            </button>
                            <button
                              className="iconbtn"
                              onClick={() => move(module, index, 1)}
                              disabled={index === module.lessons.length - 1}
                              aria-label="Move down"
                            >
                              ↓
                            </button>
                            <button
                              className="iconbtn iconbtn--danger"
                              onClick={() =>
                                setPending({
                                  path: `/admin/courses/${course.id}/lessons/${lesson.id}`,
                                  title: 'Delete this lesson?',
                                  // Quoted and shortened: a lesson whose title is
                                  // wrong is usually exactly the one being deleted,
                                  // and a 250-character title would fill the dialog.
                                  detail: `“${
                                    lesson.title.length > 80
                                      ? `${lesson.title.slice(0, 80)}…`
                                      : lesson.title
                                  }” will be removed from the curriculum.`,
                                  message: 'Lesson deleted.',
                                })
                              }
                              aria-label={`Delete the lesson ${lesson.title}`}
                              title="Deleting a lesson is not reversible from here"
                            >
                              ✕
                            </button>
                          </>
                        ) : null}
                      </div>
                      {editingLesson === lesson.id && mayManageLessons ? (
                        <LessonEditor
                          courseId={course.id}
                          lesson={lesson}
                          content={content}
                          onSaved={() => {
                            setEditingLesson(null);
                            succeed('Lesson saved.');
                          }}
                          onCancel={() => setEditingLesson(null)}
                          onError={fail}
                        />
                      ) : null}
                    </div>
                  ))}

                  {mayManageLessons ? (
                    <AddLesson
                      courseId={course.id}
                      moduleId={module.id}
                      content={content}
                      onAdded={() => succeed('Lesson added.')}
                      onError={fail}
                    />
                  ) : null}
                </div>
              ))
            )}

            {mayManageModules ? (
              <div className="card__body">
                <form onSubmit={addModule}>
                  <Field
                    label="New module"
                    placeholder="e.g. Locators and Assertions"
                    maxLength={TITLE_MAX}
                    value={moduleTitle}
                    onChange={(event) => setModuleTitle(event.target.value)}
                    hint="A short name. Longer text goes in the description below."
                  />
                  <ModuleDescriptionField
                    id="new-module-description"
                    value={moduleDescription}
                    onChange={setModuleDescription}
                  />
                  <button className="btn btn--sm" type="submit" disabled={!moduleTitle.trim()}>
                    Add module
                  </button>
                </form>
              </div>
            ) : null}

            {mayImport ? (
              <ImportFromDocument
                courseId={course.id}
                documents={documents}
                onImported={() => succeed('Lessons imported.')}
              />
            ) : null}
          </div>
        </div>

        <aside className="readiness">
          {mayEditCourse ? (
            <>
              <div className="readiness__head">Description and price</div>
              <div className="readiness__body">
                <CourseDetails course={course} onChange={load} />
              </div>

              <div className="readiness__head">Cover image</div>
              <div className="readiness__body">
                <CoverImage course={course} images={images} onChange={reload} />
              </div>

              <div className="readiness__head">How learners move through it</div>
              <div className="readiness__body">
                <NavigationModeControl course={course} onChange={load} />
              </div>
            </>
          ) : null}

          <div className="readiness__head">Publication readiness</div>
          <div className="readiness__body">
            {course.publishReadiness.canPublish ? (
              <p className="readiness__state readiness__state--ok">✓ Ready to publish</p>
            ) : (
              <>
                <p className="readiness__state readiness__state--blocked">
                  ✕ {course.publishReadiness.issues.length} blocker
                  {course.publishReadiness.issues.length === 1 ? '' : 's'}
                </p>
                <ul className="readiness__issues">
                  {course.publishReadiness.issues.map((issue) => (
                    <li key={issue.field + issue.message}>{issue.message}</li>
                  ))}
                </ul>
              </>
            )}

            {course.status === 'REVIEW' && !mayPublish ? (
              <p className="muted" style={{ font: 'var(--sx-caption)' }}>
                Waiting for a reviewer. It can be published or sent back from the review queue.
              </p>
            ) : null}

            <div className="readiness__actions">
              {/* Read it as learners will, before anyone can: the player opens a
                  draft for its authors without publishing or recording anything. */}
              {course.modules.some((module) => module.lessons.length > 0) ? (
                <Link className="btn btn--ghost btn--sm" to={`/learn/${course.id}`}>
                  Preview as learner
                </Link>
              ) : null}
              {maySubmit && CAN_SUBMIT.includes(course.status) ? (
                <button
                  className="btn btn--ghost btn--sm"
                  onClick={() =>
                    act(`/admin/courses/${course.id}/submit`, {}, 'Submitted for review.')
                  }
                  disabled={!course.publishReadiness.canPublish}
                >
                  Submit for review
                </button>
              ) : null}
              {mayPublish && CAN_PUBLISH.includes(course.status) ? (
                <button
                  className="btn btn--sm"
                  onClick={() =>
                    act(`/admin/courses/${course.id}/publish`, {}, 'Course published.')
                  }
                  disabled={!course.publishReadiness.canPublish}
                >
                  {course.status === 'ARCHIVED' ? 'Publish again' : 'Publish'}
                </button>
              ) : null}
              {course.status === 'PUBLISHED' ? (
                <>
                  <Link className="btn btn--ghost btn--sm" to={`/courses/${course.slug}`}>
                    View public page
                  </Link>
                  {mayPublish ? (
                    <button
                      className="btn btn--ghost btn--sm"
                      onClick={() =>
                        act(`/admin/courses/${course.id}/unpublish`, {}, 'Course unpublished.')
                      }
                    >
                      Unpublish
                    </button>
                  ) : null}
                </>
              ) : null}
            </div>
          </div>

          {mayDelete ? (
            <>
              <div className="readiness__head">Delete this course</div>
              <div className="readiness__body">
                <p className="muted" style={{ font: 'var(--sx-caption)', marginTop: 0 }}>
                  For a course that should never have existed. Once anyone has enrolled or earned a
                  certificate this is refused — unpublish it instead, which leaves their access
                  intact.
                </p>
                <button
                  className="btn btn--ghost btn--sm"
                  type="button"
                  onClick={() =>
                    setPending({
                      path: `/admin/courses/${course.id}`,
                      title: 'Delete this course?',
                      detail: `“${course.title}”, its ${course.modules.length} module${
                        course.modules.length === 1 ? '' : 's'
                      } and everything in them will be removed from the platform.`,
                      message: 'Course deleted.',
                      leaves: true,
                    })
                  }
                >
                  Delete course
                </button>
              </div>
            </>
          ) : null}
        </aside>
      </div>
    </>
  );
}

/**
 * The module description box, shared by "New module" and the module editor.
 *
 * Learners see this text above the module's lessons, on the course page and
 * in the player. It is where an introduction belongs: the module name is
 * capped at 250 characters, and a pasted paragraph used to be refused there
 * with "A title can be at most 250 characters".
 */
function ModuleDescriptionField({
  id,
  value,
  onChange,
}: {
  id: string;
  value: string;
  onChange: (value: string) => void;
}) {
  const left = MODULE_DESCRIPTION_MAX - value.length;
  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        Module description <span className="muted">(optional)</span>
      </label>
      <textarea
        id={id}
        className="field__input"
        rows={4}
        maxLength={MODULE_DESCRIPTION_MAX}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder="A short introduction learners read before the lessons: what this module covers and anything to do first."
      />
      <p className="field__hint">
        {value.length === 0
          ? `Up to ${MODULE_DESCRIPTION_MAX.toLocaleString()} characters. Shown to learners.`
          : `${left.toLocaleString()} characters left.`}
      </p>
    </div>
  );
}

/** Rename a module or change its description, in place. */
function ModuleEditor({
  courseId,
  module,
  onSaved,
  onCancel,
  onError,
}: {
  courseId: string;
  module: Module;
  onSaved: () => void;
  onCancel: () => void;
  onError: (error: ApiError) => void;
}) {
  const [title, setTitle] = useState(module.title);
  const [description, setDescription] = useState(module.description ?? '');
  const [busy, setBusy] = useState(false);

  async function save(event: FormEvent) {
    event.preventDefault();
    if (!title.trim()) return;
    setBusy(true);
    try {
      await api(`/admin/courses/${courseId}/modules/${module.id}`, {
        method: 'PATCH',
        // Sent even when blank: an empty description removes it.
        body: JSON.stringify({ title, description }),
      });
      onSaved();
    } catch (caught) {
      onError(caught as ApiError);
      setBusy(false);
    }
  }

  return (
    <form className="modedit" onSubmit={save}>
      <Field
        id={`module-title-${module.id}`}
        label="Module name"
        required
        maxLength={TITLE_MAX}
        value={title}
        onChange={(event) => setTitle(event.target.value)}
      />
      <ModuleDescriptionField
        id={`module-description-${module.id}`}
        value={description}
        onChange={setDescription}
      />
      <div className="row">
        <button className="btn btn--sm" type="submit" disabled={busy || !title.trim()}>
          {busy ? 'Saving…' : 'Save'}
        </button>
        <button className="btn btn--ghost btn--sm" type="button" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

/** What the lesson form edits. Minutes are a string while typed, as price is. */
interface LessonValues {
  title: string;
  type: string;
  contentItemId: string;
  externalUrl: string;
  durationMinutes: string;
  isRequired: boolean;
  isFreePreview: boolean;
}

/**
 * The fields of a lesson, shared by "Add lesson" and the lesson editor.
 *
 * The content box follows the type. A link lesson is its URL and nothing
 * else: there used to be no URL box at all, so an EXTERNAL_LINK lesson made
 * here was stuck behind "needs a URL" for ever. Every other type is offered
 * only the library items it can play, so a video lesson cannot be pointed
 * at a PDF — the API refuses that now, and the picker should not offer it.
 */
function LessonForm({
  idPrefix,
  initial,
  content,
  submitLabel,
  onSubmit,
  onCancel,
}: {
  idPrefix: string;
  initial: LessonValues;
  content: ContentOption[];
  submitLabel: string;
  /** Resolves true when saved, so the form knows whether to keep what was typed. */
  onSubmit: (values: LessonValues) => Promise<boolean>;
  onCancel: () => void;
}) {
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const link = isLinkType(form.type);
  const kinds = LESSON_KINDS[form.type] ?? [];
  // The current item stays listed even if it no longer fits, so the select
  // shows what is attached rather than silently reading "None".
  const options = content.filter(
    (item) => kinds.includes(item.kind) || item.id === form.contentItemId,
  );
  const minutes = Number(form.durationMinutes);
  const minutesInvalid =
    form.durationMinutes.trim() === '' || !Number.isInteger(minutes) || minutes < 0;

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (busy || !form.title.trim() || minutesInvalid) return;
    setBusy(true);
    const saved = await onSubmit(form);
    setBusy(false);
    if (saved) setForm(initial);
  }

  return (
    <form className="lessonedit" onSubmit={submit}>
      <div className="row">
        <Field
          id={`${idPrefix}-title`}
          label="Lesson title"
          required
          // Capped where it is typed. The server refuses a longer title, but
          // only after the form is submitted and cleared, so the message
          // arrived about text the person could no longer see.
          maxLength={TITLE_MAX}
          value={form.title}
          onChange={(event) => setForm({ ...form, title: event.target.value })}
        />
        <div className="field">
          <label className="field__label" htmlFor={`${idPrefix}-type`}>
            Type
          </label>
          <select
            id={`${idPrefix}-type`}
            className="field__input"
            value={form.type}
            onChange={(event) => {
              const type = event.target.value;
              const fits = content.some(
                (item) =>
                  item.id === form.contentItemId && (LESSON_KINDS[type] ?? []).includes(item.kind),
              );
              // Content that the new type cannot play is let go here rather
              // than refused by the server after Save.
              setForm({ ...form, type, contentItemId: fits ? form.contentItemId : '' });
            }}
          >
            {(LESSON_TYPES.includes(form.type) ? LESSON_TYPES : [...LESSON_TYPES, form.type]).map(
              (type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ),
            )}
          </select>
        </div>
      </div>

      <div className="row" style={{ marginTop: 10 }}>
        {link ? (
          <Field
            id={`${idPrefix}-url`}
            label="Link"
            type="url"
            required
            placeholder="https://…"
            maxLength={1000}
            value={form.externalUrl}
            onChange={(event) => setForm({ ...form, externalUrl: event.target.value })}
            hint="A full http:// or https:// address. Learners open it in a new tab."
          />
        ) : (
          <div className="field">
            <label className="field__label" htmlFor={`${idPrefix}-content`}>
              Content
            </label>
            <select
              id={`${idPrefix}-content`}
              className="field__input"
              value={form.contentItemId}
              onChange={(event) => setForm({ ...form, contentItemId: event.target.value })}
            >
              <option value="">None</option>
              {options.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.title} ({item.kind.toLowerCase()} · {item.status.toLowerCase()})
                </option>
              ))}
            </select>
          </div>
        )}
        <Field
          id={`${idPrefix}-minutes`}
          label="Minutes"
          type="number"
          min={0}
          step={1}
          value={form.durationMinutes}
          invalid={minutesInvalid}
          onChange={(event) => setForm({ ...form, durationMinutes: event.target.value })}
        />
      </div>

      <label className="lessonedit__check">
        <input
          type="checkbox"
          checked={form.isRequired}
          onChange={(event) => setForm({ ...form, isRequired: event.target.checked })}
        />
        Required — counts toward completing the course
      </label>
      <label className="lessonedit__check">
        <input
          type="checkbox"
          checked={form.isFreePreview}
          onChange={(event) => setForm({ ...form, isFreePreview: event.target.checked })}
        />
        Free preview — viewable without an account, once its content is published
      </label>

      <div className="row">
        <button
          className="btn btn--sm"
          type="submit"
          disabled={busy || !form.title.trim() || minutesInvalid}
        >
          {busy ? 'Saving…' : submitLabel}
        </button>
        <button className="btn btn--ghost btn--sm" type="button" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

const NEW_LESSON: LessonValues = {
  title: '',
  type: 'RICH_TEXT',
  contentItemId: '',
  externalUrl: '',
  durationMinutes: '10',
  isRequired: true,
  isFreePreview: false,
};

function AddLesson({
  courseId,
  moduleId,
  content,
  onAdded,
  onError,
}: {
  courseId: string;
  moduleId: string;
  content: ContentOption[];
  onAdded: () => void;
  onError: (error: ApiError) => void;
}) {
  const [open, setOpen] = useState(false);

  async function add(values: LessonValues): Promise<boolean> {
    const link = isLinkType(values.type);
    try {
      await api(`/admin/courses/${courseId}/modules/${moduleId}/lessons`, {
        method: 'POST',
        body: JSON.stringify({
          title: values.title,
          type: values.type,
          contentItemId: link ? undefined : values.contentItemId || undefined,
          externalUrl: link ? values.externalUrl.trim() || undefined : undefined,
          durationMinutes: Number(values.durationMinutes),
          isRequired: values.isRequired,
          isFreePreview: values.isFreePreview,
        }),
      });
      setOpen(false);
      onAdded();
      return true;
    } catch (caught) {
      onError(caught as ApiError);
      return false;
    }
  }

  if (!open) {
    return (
      <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--line)' }}>
        <button className="iconbtn" onClick={() => setOpen(true)}>
          + Add lesson
        </button>
      </div>
    );
  }

  return (
    <LessonForm
      idPrefix={`new-lesson-${moduleId}`}
      initial={NEW_LESSON}
      content={content}
      submitLabel="Add lesson"
      onSubmit={add}
      onCancel={() => setOpen(false)}
    />
  );
}

/**
 * Change a lesson in place, as `ModuleEditor` does for a module.
 *
 * There was no way to edit a lesson from here at all: a mistyped title, a
 * missing URL or the wrong file could only be fixed by deleting the lesson
 * and adding it again — and only if somebody knew to.
 *
 * Every field is sent. Content and URL are sent as null when empty, which
 * the API reads as "remove it": that is how content is detached.
 */
function LessonEditor({
  courseId,
  lesson,
  content,
  onSaved,
  onCancel,
  onError,
}: {
  courseId: string;
  lesson: Lesson;
  content: ContentOption[];
  onSaved: () => void;
  onCancel: () => void;
  onError: (error: ApiError) => void;
}) {
  const initial: LessonValues = {
    title: lesson.title,
    type: lesson.type,
    contentItemId: lesson.contentItemId ?? '',
    externalUrl: lesson.externalUrl ?? '',
    durationMinutes: String(lesson.durationMinutes),
    isRequired: lesson.isRequired,
    isFreePreview: lesson.isFreePreview,
  };

  async function save(values: LessonValues): Promise<boolean> {
    const link = isLinkType(values.type);
    try {
      await api(`/admin/courses/${courseId}/lessons/${lesson.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          title: values.title,
          type: values.type,
          contentItemId: link ? null : values.contentItemId || null,
          externalUrl: link ? values.externalUrl.trim() || null : null,
          durationMinutes: Number(values.durationMinutes),
          isRequired: values.isRequired,
          isFreePreview: values.isFreePreview,
        }),
      });
      onSaved();
      return true;
    } catch (caught) {
      onError(caught as ApiError);
      return false;
    }
  }

  return (
    <LessonForm
      idPrefix={`lesson-${lesson.id}`}
      initial={initial}
      content={content}
      submitLabel="Save"
      onSubmit={save}
      onCancel={onCancel}
    />
  );
}

/**
 * The full description and the price.
 *
 * Publication demands both — a description of at least 40 characters, and
 * either a price above zero or the course marked free — and neither had a
 * box anywhere in the application. The API has accepted `longDescription`,
 * `priceMinor`, `currency` and `isFree` since the first release, the create
 * form asks for title, category and short description only, and the builder
 * patched nothing but the cover and the navigation mode. So a course built
 * entirely through the interface could be taken right up to Submit for
 * review and then stop, against two blockers with nothing to press.
 *
 * That is the same gap `CoverImage` below was written to close, left open in
 * two more places.
 *
 * Price is held as a string while being typed, because a number input bound
 * to a number cannot hold an empty box: clearing it to retype gives NaN,
 * which lands as a zero and reads as free. It is parsed once, on save.
 */
function CourseDetails({ course, onChange }: { course: CourseDetail; onChange: () => void }) {
  const [description, setDescription] = useState(course.longDescription ?? '');
  const [free, setFree] = useState(course.isFree);
  // Minor units in, major units on screen: nobody prices a course in paise.
  const [price, setPrice] = useState(course.priceMinor > 0 ? String(course.priceMinor / 100) : '');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  // The panel is re-created on every reload of the course, so anything typed
  // and not yet saved has to survive — but a save elsewhere on the page must
  // not leave stale text here either. Resetting on the stored values is the
  // honest compromise: what is shown is what the server last accepted.
  useEffect(() => {
    setDescription(course.longDescription ?? '');
    setFree(course.isFree);
    setPrice(course.priceMinor > 0 ? String(course.priceMinor / 100) : '');
  }, [course.longDescription, course.isFree, course.priceMinor]);

  const trimmed = description.trim();
  const amount = Number(price);
  const priceInvalid = !free && (price.trim() === '' || !Number.isFinite(amount) || amount <= 0);

  async function save() {
    if (busy) return;
    setBusy(true);
    setProblem(null);
    setSaved(false);
    try {
      await api(`/admin/courses/${course.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          longDescription: trimmed,
          isFree: free,
          // A free course keeps whatever price it had; the publish rule reads
          // isFree first, and zeroing it would lose the figure on a mis-tick.
          ...(free ? {} : { priceMinor: Math.round(amount * 100) }),
        }),
      });
      setSaved(true);
      onChange();
    } catch (caught) {
      setProblem((caught as ApiError).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="stack">
      <div className="field">
        <label className="field__label" htmlFor="course-long-description">
          Full description
        </label>
        <textarea
          id="course-long-description"
          className="field__input"
          rows={5}
          maxLength={20000}
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          placeholder="What the course covers, who it is for, and what a learner can do by the end."
        />
        <p className="field__hint">
          {trimmed.length < 40
            ? `At least 40 characters — ${40 - trimmed.length} to go.`
            : `${trimmed.length} characters.`}
        </p>
      </div>

      <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
        <input type="checkbox" checked={free} onChange={(event) => setFree(event.target.checked)} />
        This course is free
      </label>

      {free ? null : (
        <Field
          label={`Price (${course.currency})`}
          type="number"
          min="0"
          step="0.01"
          inputMode="decimal"
          value={price}
          invalid={priceInvalid}
          onChange={(event) => setPrice(event.target.value)}
          hint="Enter the amount a learner pays, not the minor units."
        />
      )}

      {problem ? <Alert kind="error" title={problem} /> : null}
      {saved && !problem ? <Alert kind="success" title="Saved." /> : null}

      <div className="row">
        <button
          className="btn btn--sm"
          type="button"
          disabled={busy || priceInvalid}
          onClick={() => void save()}
        >
          {busy ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}

/**
 * Free or sequential navigation (BRD F-LRN-03).
 *
 * Sequential is enforced by the API on every lesson request, not by hiding
 * links in the player, so switching it here genuinely locks the content.
 */
function NavigationModeControl({
  course,
  onChange,
}: {
  course: CourseDetail;
  onChange: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const sequential = course.navigationMode === 'SEQUENTIAL';

  async function set(mode: 'FREE' | 'SEQUENTIAL') {
    if (busy || mode === course.navigationMode) return;
    setBusy(true);
    setProblem(null);
    try {
      await api(`/admin/courses/${course.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ navigationMode: mode }),
      });
      onChange();
    } catch (caught) {
      // There was a `finally` and no `catch`, so a refusal escaped the click
      // handler as an uncaught rejection and the button simply did nothing.
      setProblem((caught as ApiError).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="stack">
      <div className="chips" role="radiogroup" aria-label="Navigation mode">
        <button
          role="radio"
          aria-checked={!sequential}
          className={`pill ${!sequential ? 'pill--active' : 'pill--inactive'}`}
          disabled={busy}
          onClick={() => void set('FREE')}
        >
          Any order
        </button>
        <button
          role="radio"
          aria-checked={sequential}
          className={`pill ${sequential ? 'pill--active' : 'pill--inactive'}`}
          disabled={busy}
          onClick={() => void set('SEQUENTIAL')}
        >
          In order
        </button>
      </div>
      <p className="muted" style={{ font: 'var(--sx-caption)' }}>
        {sequential
          ? 'Each lesson stays locked until the previous required one is complete. Free previews are always open.'
          : 'Learners may open any lesson at any time.'}
      </p>
      {problem ? <Alert kind="error" title={problem} /> : null}
    </div>
  );
}

/**
 * The course cover.
 *
 * A course cannot be published without one, and until now there was nowhere in
 * the application to set it: the API accepted a thumbnail id and the publish
 * check demanded one, but no screen offered either. A course built entirely
 * through the interface could never be published.
 *
 * Both routes are offered because both are real. Uploading is what happens the
 * first time a course is built; choosing an existing image is what happens
 * when a series shares artwork, and re-uploading the same file for each course
 * is how a library fills with duplicates.
 */
function CoverImage({
  course,
  images,
  onChange,
}: {
  course: CourseDetail;
  images: ContentOption[];
  onChange: () => void;
}) {
  const [busy, setBusy] = useState<'upload' | 'pick' | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const { can } = useAuth();

  /** See settleUpload: an upload starts as a draft, and a live cover cannot be one. */
  const settle = (itemId: string) => settleUpload(itemId, can);


  const current = images.find((image) => image.id === course.thumbnailId) ?? null;

  // The file is served through a short-lived signed link rather than a public
  // path, so the preview has to ask for one each time the cover changes.
  useEffect(() => {
    let live = true;
    if (!course.thumbnailId) {
      setPreview(null);
      return;
    }
    api<{ url: string }>(`/content/${course.thumbnailId}/link`)
      .then((result) => {
        if (live) setPreview(result.url);
      })
      .catch(() => {
        if (live) setPreview(null);
      });
    return () => {
      live = false;
    };
  }, [course.thumbnailId]);

  async function assign(thumbnailId: string) {
    await api(`/admin/courses/${course.id}`, {
      method: 'PATCH',
      body: JSON.stringify({ thumbnailId }),
    });
    onChange();
  }

  async function pick(thumbnailId: string) {
    if (!thumbnailId) return;
    setBusy('pick');
    setProblem(null);
    try {
      await assign(thumbnailId);
    } catch (caught) {
      setProblem((caught as ApiError).message);
    } finally {
      setBusy(null);
    }
  }

  async function upload(event: FormEvent<HTMLInputElement>) {
    const file = event.currentTarget.files?.[0];
    if (!file) return;

    setBusy('upload');
    setProblem(null);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('title', `${course.title} — cover`);

      // FormData sets its own multipart boundary, so the JSON default in the
      // fetch wrapper must not be applied here.
      const response = await apiFetch('/content/upload', {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('sx.access') ?? ''}` },
        body: form,
      });
      const payload = await response.json();
      if (!response.ok) throw payload as ApiError;

      // Uploading it is only half the job — it still has to become this
      // course's cover, and a half-done upload is worse than none.
      // Settled before it is assigned: on a live course the API refuses a
      // cover that is not published.
      await settle((payload as ContentOption).id);
      await assign((payload as ContentOption).id);
      if (input.current) input.current.value = '';
    } catch (caught) {
      setProblem((caught as ApiError).message ?? 'The image could not be uploaded.');
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="cover">
      {preview ? (
        <img className="cover__preview" src={preview} alt={`Cover for ${course.title}`} />
      ) : (
        <div className="cover__preview cover__preview--empty">
          {course.thumbnailId ? 'Cover set, but it cannot be shown' : 'No cover yet'}
        </div>
      )}

      {current && current.servable === false ? (
        <p className="cover__warn">
          This image is waiting on a virus scan, so learners will not see it until it clears.
        </p>
      ) : null}
      {/* An upload starts as a draft. It can be the cover straight away, but
          the course cannot be published until the image is. */}
      {course.thumbnailId && course.thumbnailStatus && course.thumbnailStatus !== 'PUBLISHED' ? (
        <p className="cover__warn">
          This image is {course.thumbnailStatus.toLowerCase()}. Publish it in the content library
          before the course can be published.
        </p>
      ) : null}

      <label className="cover__action">
        <span className="btn btn--sm btn--ghost">
          {busy === 'upload'
            ? 'Uploading…'
            : current
              ? 'Replace with an upload'
              : 'Upload an image'}
        </span>
        <input
          ref={input}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          disabled={busy !== null}
          onChange={(event) => void upload(event)}
        />
      </label>

      <label className="field__label" htmlFor="cover-pick">
        Or choose one already in the library
      </label>
      <select
        className="field__input"
        id="cover-pick"
        value={course.thumbnailId ?? ''}
        disabled={busy !== null || images.length === 0}
        onChange={(event) => void pick(event.target.value)}
      >
        <option value="">
          {images.length === 0 ? 'No images in the library yet' : 'Choose an image…'}
        </option>
        {images.map((image) => (
          <option key={image.id} value={image.id}>
            {image.title}
            {image.servable === false ? ' (not yet scanned)' : ''}
          </option>
        ))}
      </select>

      {problem ? <p className="cover__warn">{problem}</p> : null}
    </div>
  );
}
